import { memo, useContext, useEffect, useRef, useState } from 'react';

import { LabelValueGroup } from 'components/common/LabelValueGroup';
import { Button } from 'components/design-system/Button';
import { Slideout, SlideoutCommonProps } from 'components/design-system/Slideout';
import { FinalOfferInfoBottomSheet } from 'components/finalOffer/FinalOfferInfoBottomSheet';
import { FinalOfferPropositionBottomSheet } from 'components/finalOffer/FinalOfferPropositionBottomSheet';
import { SellerProfileContext } from 'context/sellerProfile';
import { useHideIntercom } from 'hooks';
import { INITIATOR, Offer, OFFER_STATUS } from 'types';
import { formatPrice, getListingBreakdownPrice } from 'utils/text-helpers';

import { buyersResponseMap, sellerResponseMap, statusWithTextUpdate } from './constants';
import { CreateRequestBottomSheet } from './CreateRequestBottomSheet';
import { OfferModalListingInfo } from './OfferModalListingInfo';
import { PriceHistoryItem } from './PriceHistoryItem';
import { useCreateRequestBottomSheet } from './useCreateRequestBottomSheet';

type OfferModalProps = {
  offer: Offer;
  onClickDecline: () => Promise<Offer | undefined>;
  onClickAccept: () => Promise<Offer | undefined>;
  onClickCounter: () => void;
  isSeller?: boolean;
} & SlideoutCommonProps;

const OfferModalComponent: React.FC<OfferModalProps> = ({
  isOpen,
  closeModal,
  offer,
  onClickDecline,
  onClickAccept,
  onClickCounter,
  isSeller,
  onClickPrevious
}) => {
  const conversationContainerRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [processingType, setProcessingType] = useState<string | null>(null);

  const [bottomSheetToOpen, setBottomSheetToOpen] = useState<'finalOfferProposition' | 'finalOfferInfo' | null>(
    !!isSeller && 'dealTrigger' in offer ? 'finalOfferProposition' : null
  );

  const openFinalOfferPropositionBottomSheet = () => setBottomSheetToOpen('finalOfferProposition');
  const openFinalOfferInfoBottomSheet = () => setBottomSheetToOpen('finalOfferInfo');

  const { isRequestSheetVisible, setIsRequestSheetVisible, titleText } = useCreateRequestBottomSheet(
    offer,
    !!isSeller,
    !!isOpen
  );

  const ctaClick = async (type: 'accept' | 'decline') => {
    setProcessingType(type);
    const action = type === 'accept' ? onClickAccept : onClickDecline;

    return action().finally(() => {
      setProcessingType(null);
    });
  };

  const priceHistory = [...(offer.priceHistory || [])].reverse();
  const mostRecentCounter = priceHistory[priceHistory.length - 1];
  const mostRecentInitiator = mostRecentCounter?.initiator;
  const mostRecentIsBuyer = mostRecentInitiator === INITIATOR.Buyer;
  const { sellerProfile, sellerLevels } = useContext(SellerProfileContext);

  const buyerCanRespond = offer.status === OFFER_STATUS.AWAITING_BUYER_RESPONSE && !isSeller;
  const sellerCanRespond = offer.status === OFFER_STATUS.AWAITING_SELLER_RESPONSE && isSeller;

  const responsesMap = isSeller ? sellerResponseMap : buyersResponseMap;
  const statusUpdateText = responsesMap[offer.status]?.(mostRecentIsBuyer);

  const { earnings } = getListingBreakdownPrice(
    {
      priceCents: mostRecentCounter?.priceCents || 0,
      overrideCommissionRateBips: offer.listing.overrideCommissionRateBips,
      overrideSellerFeeCents: offer.listing.overrideSellerFeeCents,
      overrideMinTotalFeeCents: offer.listing.overrideMinTotalFeeCents
    },
    sellerProfile,
    sellerLevels
  );

  useEffect(() => {
    if (scrollContainerRef.current && conversationContainerRef.current && isOpen) {
      const { height } = conversationContainerRef.current.getBoundingClientRect();

      scrollContainerRef.current?.scrollTo({ top: height });
    }
  }, [isOpen]);

  useHideIntercom();

  return (
    <Slideout
      title
      bodyClassName='d-flex flex-column'
      isOpen={isOpen}
      closeModal={closeModal}
      onClickPrevious={onClickPrevious}
    >
      {bottomSheetToOpen === 'finalOfferProposition' && (
        <FinalOfferPropositionBottomSheet offer={offer} openFinalOfferInfoBottomSheet={openFinalOfferInfoBottomSheet} />
      )}
      {bottomSheetToOpen === 'finalOfferInfo' && (
        <FinalOfferInfoBottomSheet onBack={openFinalOfferPropositionBottomSheet} />
      )}
      <div className='px-0 flex-1 d-flex flex-column justify-content-between'>
        <div className='d-flex flex-column h-100'>
          <OfferModalListingInfo offer={offer} />
          <div className='overflow-auto position-relative h-100' ref={scrollContainerRef}>
            <div className='position-absolute w-100' ref={conversationContainerRef}>
              {priceHistory.map((price, idx) => (
                <PriceHistoryItem
                  key={`priceHistory-${idx}-${price.priceCents}`}
                  idx={idx}
                  isSeller={isSeller}
                  offer={offer}
                  price={price}
                  priceHistory={priceHistory}
                  mostRecentIsBuyer={mostRecentIsBuyer}
                />
              ))}
            </div>
          </div>
        </div>
        <div>
          {mostRecentIsBuyer && isSeller && mostRecentCounter && (
            <div className='d-flex w-100 mx-auto'>
              <LabelValueGroup label='BUYER OFFER' className='w-50 letter-spacing-08'>
                <span className='fs-16px line-height-24px'>{formatPrice(mostRecentCounter.priceCents)}</span>
              </LabelValueGroup>
              <LabelValueGroup label='YOUR NET PAYOUT' className='w-50 letter-spacing-08'>
                <span className='fs-16px line-height-24px'>{formatPrice(earnings)}</span>
              </LabelValueGroup>
            </div>
          )}
          {statusWithTextUpdate.includes(offer.status) && (
            <div className='mt-1 riforma-regular text-secondary text-center fs-14px line-height-20px'>
              {statusUpdateText}
            </div>
          )}
          {(buyerCanRespond || sellerCanRespond) && (
            <div>
              <Button
                loading={processingType === 'accept'}
                className='w-100 border rounded-2 bg-primary text-center riforma-regular mt-3 mb-2 mb-md-3 mx-auto py-2 fs-14px fs-md-16px'
                onClick={() => ctaClick('accept')}
              >
                <div className='text-white'>Accept offer</div>
                <div className='text-secondary'>{formatPrice(mostRecentCounter?.priceCents ?? offer.priceCents)}</div>
              </Button>
              <div className='w-100 d-flex gap-2 gap-md-3 text-center riforma-regular text-primary mx-auto'>
                <Button
                  className='w-50 border rounded-2 p-3 fs-14px fs-md-16px'
                  variant='secondary'
                  onClick={() => ctaClick('decline')}
                  loading={processingType === 'decline'}
                >
                  Decline
                </Button>
                <Button
                  className='w-50 border rounded-2 p-3 fs-14px fs-md-16px'
                  variant='secondary'
                  onClick={onClickCounter}
                >
                  Counter offer
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
      {isRequestSheetVisible && mostRecentCounter && offer?.listing?.model && titleText && (
        <CreateRequestBottomSheet
          titleText={titleText}
          price={mostRecentCounter?.priceCents}
          model={offer.listing.model}
          onClose={() => setIsRequestSheetVisible(false)}
          closeModal={closeModal}
        />
      )}
    </Slideout>
  );
};

// todo refactor this code so it does not render all slideouts
export const OfferModal = memo((props: OfferModalProps) => {
  if (!props.offer) {
    return null;
  }

  return <OfferModalComponent {...props} />;
});
